import React, { useEffect, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { Alert, Button, Spinner, TextInput } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  AddLanguageFilterTerm,
  ArchiveLanguageFilterTerm,
  BannedTerm,
  GetLanguageFilterTerms,
  GetTicketBans,
  IssueTicketBan,
  LiftTicketBan,
  PortalRoleMemberHit,
  SearchTicketBanTargets,
  TicketBan,
} from '../../services/dtos';
import { fmtDateTime } from './supportShared';

// T7 — Conduct controls (vision Feature 5): the banned-word list and the
// ticket-ban tool/log. Server-gated to BanAuthority (Admin passes via the
// superuser bypass); the route guard here is cosmetic. Every change lands in
// the audit trail; both tables are history (archive/lift, never delete).

const INPUT_CLS =
  'block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500';

const SupportAdminPage: React.FC = () => (
  <div className="mx-auto w-full flex max-w-4xl flex-col gap-6 dark:text-white">
    <div>
      <h2 className="text-2xl font-bold">Conduct Controls</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        The banned-word filter and ticket-system bans. Every change here is
        recorded in the audit trail.
      </p>
    </div>
    <TermsCard />
    <BansCard />
  </div>
);

// ── banned words & phrases ──────────────────────────────────────────────────

const TermsCard: React.FC = () => {
  const [terms, setTerms] = useState<BannedTerm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newTerm, setNewTerm] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    apiServices.client
      .get(new GetLanguageFilterTerms())
      .then((r) => setTerms(r.terms || []))
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Failed to load the term list')
      );
  }, []);

  const run = async (fn: () => Promise<{ terms?: BannedTerm[] }>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      setTerms(r.terms || []);
    } catch (e: any) {
      setError(e?.responseStatus?.message || 'That change failed.');
    } finally {
      setBusy(false);
    }
  };

  const add = () =>
    run(async () => {
      const req = new AddLanguageFilterTerm();
      req.term = newTerm.trim();
      if (newNotes.trim()) req.notes = newNotes.trim();
      const r = await apiServices.client.post(req);
      setNewTerm('');
      setNewNotes('');
      return r;
    });

  const archive = (t: BannedTerm) => {
    if (
      !window.confirm(
        `Remove "${t.term}" from the active filter list?\n\nIt stays in the history below — the list is a record, not a setting.`
      )
    )
      return;
    run(() => {
      const req = new ArchiveLanguageFilterTerm();
      req.id = t.id;
      return apiServices.client.post(req);
    });
  };

  const active = (terms || []).filter((t) => !t.archivedAtUtc);
  const archived = (terms || []).filter((t) => t.archivedAtUtc);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-4">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white">
          Banned words &amp; phrases
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Submissions and replies containing these are blocked server-side and
          the member is asked to reword. Matching is case-insensitive and
          whole-word; phrases match across spacing and line breaks.
        </p>
      </div>

      {error && (
        <Alert color="failure" icon={HiInformationCircle}>
          {error}
        </Alert>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="sm:flex-1">
          <TextInput
            id="new-term"
            value={newTerm}
            maxLength={200}
            placeholder="Word or phrase to ban…"
            onChange={(e: any) => setNewTerm(e.target.value)}
          />
        </div>
        <div className="sm:flex-1">
          <TextInput
            id="new-term-notes"
            value={newNotes}
            maxLength={200}
            placeholder="Notes (optional — why it's on the list)"
            onChange={(e: any) => setNewNotes(e.target.value)}
          />
        </div>
        <Button onClick={add} disabled={busy || !newTerm.trim()}>
          {busy ? <Spinner size="sm" /> : 'Add'}
        </Button>
      </div>

      {!terms && !error && <Spinner color="info" />}

      {terms && active.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          The list is empty — nothing is being filtered.
        </p>
      )}

      {active.length > 0 && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {active.map((t) => (
            <li
              key={t.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
            >
              <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t.term}
              </span>
              {t.notes && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t.notes}
                </span>
              )}
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                added by {t.createdByName || '—'} · {fmtDateTime(t.createdAtUtc)}
              </span>
              <Button
                size="xs"
                color="gray"
                onClick={() => archive(t)}
                disabled={busy}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {archived.length > 0 && (
        <div>
          <button
            className="text-sm text-indigo-600 dark:text-indigo-400"
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? '▾' : '▸'} Removed terms ({archived.length}) — change
            history
          </button>
          {showArchived && (
            <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-800">
              {archived.map((t) => (
                <li
                  key={t.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-xs text-gray-500 dark:text-gray-400"
                >
                  <span className="font-mono line-through">{t.term}</span>
                  <span>
                    added by {t.createdByName || '—'} ·{' '}
                    {fmtDateTime(t.createdAtUtc)}
                  </span>
                  <span>
                    removed by {t.archivedByName || '—'} ·{' '}
                    {fmtDateTime(t.archivedAtUtc)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ── ticket-system bans ──────────────────────────────────────────────────────

const BansCard: React.FC = () => {
  const [bans, setBans] = useState<TicketBan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Form H: referent lookup + reason
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<PortalRoleMemberHit[]>([]);
  const [target, setTarget] = useState<PortalRoleMemberHit | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    apiServices.client
      .get(new GetTicketBans())
      .then((r) => setBans(r.bans || []))
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Failed to load the ban log')
      );
  }, []);

  // debounced member search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      return;
    }
    const handle = setTimeout(() => {
      const req = new SearchTicketBanTargets();
      req.q = q;
      apiServices.client
        .get(req)
        .then(setHits)
        .catch(() => setHits([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const run = async (fn: () => Promise<{ bans?: TicketBan[] }>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      setBans(r.bans || []);
    } catch (e: any) {
      setError(e?.responseStatus?.message || 'That action failed.');
    } finally {
      setBusy(false);
    }
  };

  const issue = () => {
    if (!target) return;
    if (
      !window.confirm(
        `Ban ${target.name} from the ticket system?\n\nThey keep their schedule, availability, and calls — this closes support requests only. They will see the date and who to contact, never the reason.`
      )
    )
      return;
    run(async () => {
      const req = new IssueTicketBan();
      req.targetUniqueMemberId = target.uniqueMemberId;
      req.reason = reason.trim();
      const r = await apiServices.client.post(req);
      setTarget(null);
      setQuery('');
      setReason('');
      return r;
    });
  };

  const lift = (b: TicketBan) => {
    const note = window.prompt(
      `Lift the ticket-system ban for ${b.targetName}?\n\nOptional note for the log (OK to leave blank):`
    );
    if (note === null) return;
    run(() => {
      const req = new LiftTicketBan();
      req.banId = b.id;
      if (note.trim()) req.note = note.trim();
      return apiServices.client.post(req);
    });
  };

  const activeBans = (bans || []).filter((b) => !b.liftedAtUtc);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-4">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white">
          Ticket-system bans
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          A ban closes support requests only — schedule, availability, and
          calls are untouched. The banned member sees the date and who to
          contact; the reason stays in this log.
        </p>
      </div>

      {error && (
        <Alert color="failure" icon={HiInformationCircle}>
          {error}
        </Alert>
      )}

      {/* issue (Form H: referent + reason; scope fixed, issuer captured) */}
      <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 flex flex-col gap-3">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
          Ban a referent
        </span>
        {!target ? (
          <>
            <TextInput
              id="ban-search"
              value={query}
              placeholder="Search by name, email, or member id…"
              onChange={(e: any) => setQuery(e.target.value)}
            />
            {hits.length > 0 && (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800 rounded border border-gray-200 dark:border-gray-700">
                {hits.map((h) => (
                  <li key={h.uniqueMemberId}>
                    <button
                      className="flex w-full flex-wrap items-baseline gap-x-3 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => setTarget(h)}
                    >
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {h.name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {h.email || h.uniqueMemberId} · {h.membershipStatus}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {target.name}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {target.email || target.uniqueMemberId}
              </span>
              <button
                className="text-xs text-indigo-600 dark:text-indigo-400"
                onClick={() => setTarget(null)}
              >
                change
              </button>
            </div>
            <textarea
              rows={2}
              className={INPUT_CLS}
              placeholder="Reason (required — recorded in the ban log, never shown to the member)…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div>
              <Button
                color="failure"
                onClick={issue}
                disabled={busy || !reason.trim()}
              >
                {busy ? <Spinner size="sm" /> : 'Ban from ticket system'}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* log: who, whom, when, why, lifted-by/when */}
      {!bans && !error && <Spinner color="info" />}
      {bans && bans.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No bans have ever been issued.
        </p>
      )}
      {bans && bans.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <tr>
                <th className="py-2 pr-3">Member</th>
                <th className="py-2 pr-3">Banned</th>
                <th className="py-2 pr-3">Reason</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {bans.map((b) => (
                <tr key={b.id}>
                  <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                    {b.targetName}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">
                    by {b.issuedByName || '—'} · {fmtDateTime(b.issuedAtUtc)}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-600 dark:text-gray-300 break-words">
                    {b.reason}
                    {b.liftNote && (
                      <span className="block text-gray-400 dark:text-gray-500">
                        lift note: {b.liftNote}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {b.liftedAtUtc ? (
                      <span className="text-gray-500 dark:text-gray-400">
                        Lifted by {b.liftedByName || '—'} ·{' '}
                        {fmtDateTime(b.liftedAtUtc)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 font-medium text-red-800 dark:bg-red-900 dark:text-red-200">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {!b.liftedAtUtc && (
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() => lift(b)}
                        disabled={busy}
                      >
                        Lift
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeBans.length > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {activeBans.length} active{' '}
          {activeBans.length === 1 ? 'ban' : 'bans'} — the same people who
          issue bans can lift them.
        </p>
      )}
    </div>
  );
};

export default SupportAdminPage;
